import { AbDimensionCandidate } from '../graphql/queries/getAbDimensionCandidates';

/** AB 映射维度行 = 一组「联系人 + 联系方式 + 渠道」。 */
export interface AbDimensionRow {
  /**
   * 唯一键 = 联系人 + 电话 + 渠道 id（供表格 rowKey / 选中态）。
   *
   * **必须含联系人姓名**：条目 `contact_person_json` 是 `markStuck` 从多个订单攒出来的，
   * 同一发货点多个联系人共用一个总机号是常态（条目 467 实测：4 个联系人里 3 个共用 5109364838）。
   * 只用 `phone + channel` 会让 4×2=8 行只剩 4 个 distinct key，连带三个后果：
   * ① 勾 Eric 会同时勾上同号的 HAZEL/FREYA，运营无法只选一个联系人；
   * ② `fullySelected`（`selectedDimKeys.length === dimensionRows.length`）恒为 false
   *    ⇒ 提交只写 `PROCESS_PARTIAL`，条目永远不关闭；
   * ③ `key` 在同一列表里重复，React reconciliation 出错。
   *
   * 与 `findActiveMatch` 的命中键口径一致（hub + core_hash + contact_name + phone + channel），
   * 两者必须同步改：只改这里会解锁部分映射，而命中侧仍只比电话+渠道 ⇒ 未映射的同号联系人
   * 会"搭便车"被路由到 B。
   */
  key: string;
  contactName?: string;
  contactPhone: string;
  phoneCountryCode?: string;
  channelId: number;
  /** 已映射到的 B 站点 id；非空 = 本维度已处理过（默认不勾、但计入 fullySelected） */
  mappedTo?: number;
}

/**
 * 后端返回的真实三元组 → 维度行（2.1 §10.11）。
 *
 * ## 取代了原先的「条目快照 ∪ 站点档案 的笛卡尔积」
 *
 * AB 命中比的是 `opi.contact_name / contact_phone / channel_id`，所以维度的唯一正确来源是订单。
 * 站点档案可被运营手动编辑（与订单不一致 ⇒ 选了写出死映射）；条目快照只在"新单卡单"时追加而
 * 能命中站点的单永远不卡单（⇒ 永久缺维度）。两者都不是订单事实，详见
 * `getAbDimensionCandidates.ts` 的说明。
 *
 * ## key 必须与旧实现同格式
 *
 * `<name>__<phone>__<channelId>` —— 与命中侧
 * `findActiveMatch(hub, coreHash, contact_name, contact_phone, channel_id)` 逐字对齐。
 * **含姓名**：同一发货点多联系人共用总机号是常态，漏这一维会让"勾一个连带勾上同号的另一个"。
 *
 * 电话或渠道为空的候选直接丢弃：命中侧对这两维用等值比较，缺任一维必然命不中，
 * 列出来只会让运营选出死映射（后端已过滤，这里是防御性重复）。
 */
export function buildRowsFromCandidates(
  candidates: AbDimensionCandidate[] | undefined,
): AbDimensionRow[] {
  return (candidates ?? [])
    .filter((c) => !!c.contactPhone && String(c.contactPhone).trim() !== '' && c.channelId != null)
    .map((c) => {
      const name = (c.contactName ?? '').trim();
      const phone = String(c.contactPhone);
      const channelId = Number(c.channelId);
      return {
        key: `${name}__${phone}__${channelId}`,
        contactName: c.contactName ?? undefined,
        contactPhone: phone,
        channelId,
        mappedTo:
          c.mappedCooperationWarehouseId == null ? undefined : Number(c.mappedCooperationWarehouseId),
      };
    });
}
